import { UNIT_TYPE_VALUES, unitCategories, unitsJson } from "@corca/data";

import { mapUnitToSetChildren } from "@/components/doc/right-sidebar/active-node-style/fields/unitUtils";
import { prioritizeSuggestionRules } from "@/input/utils/editOperators";
import { isUnitChild } from "@/models/nodes/utils";

import { checkFilterRules } from "./utils/filterRules";
import { filterSuggestions } from "./utils/filterSuggestions";
import { checkNodeCondition } from "./utils/nodeCondition";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type { SuggestionProvider, SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import type { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import type {
  SubstitutionRule,
  UnitNodePlacement,
  UnitWithType,
  FilterRule,
  KeywordLang,
} from "@corca/data";

const unitsWithType: UnitWithType[] = UNIT_TYPE_VALUES.flatMap((unitType) =>
  unitsJson
    .filter((unit) => unitCategories(unit).includes(unitType))
    .map((unit) => ({ ...unit, unitType })),
);

function mapValueToUnitSuggestion(
  value: string,
  unit: UnitWithType,
  applyTo: Extract<SubstitutionRule["applyTo"], "nodeBefore" | "nodeAfter"> = "nodeBefore",
  newNode?: boolean,
  nodeType: "number" | "variable" | "constant" = "number",
): SingleSuggestion {
  const { placement, name, keywords } = unit;

  const setChildren = mapUnitToSetChildren(unit);

  const substitution: SubstitutionRule | undefined = newNode
    ? undefined
    : {
        applyTo,
        cursorPlacement: "child:0:end",
        setChildren,
      };

  const filterRules: FilterRule[] | undefined = newNode
    ? undefined
    : [
        {
          condition: {
            [applyTo]: {
              hasType: ["number", "variable", "constant"],
              hasUnit: [`not:${placement}`],
            },
          },
        },
      ];

  return {
    type: "single",
    item: {
      type: nodeType,
      intlName: nodeType,
      category: name ?? "Unit",
      symbol: value,
      keywords: normalizeSuggestionKeywords({ keywords }),
      setChildren: newNode ? setChildren : undefined,
    },
    filterRules,
    priorityRules: [
      {
        condition: {
          [applyTo]: {
            hasType: ["number"],
          },
        },
        priority: 1,
      },
    ],
    substitution,
    priority: 0,
    popupGroup: "units",
    canBeAutocompleted: {
      text: { regex: "\\P{Letter}$" },
      math: { regex: "\\P{Letter}$" },
    },
  };
}

function createCustomUnitSuggestion(
  symbol: string,
  unitInput: string,
  placement: UnitNodePlacement,
  newNode?: boolean,
): SingleSuggestion {
  const isPrefix = placement === "prefix";
  return mapValueToUnitSuggestion(
    symbol,
    {
      category: [],
      placement,
      unitType: undefined,
      name: "Unit",
      children: [
        {
          type: "plainText",
          props: {
            value: unitInput,
          },
        },
      ],
    },
    isPrefix ? "nodeAfter" : "nodeBefore",
    newNode,
  );
}

const PREFIX_UNIT_REGEX = /^(\p{Currency_Symbol}+)/u;

const POSTFIX_UNIT_REGEX = /^((?:\p{Letter})+)/u;

const PREFIX_UNIT_WITH_NUMBER_REGEX = /^((?:\p{Letter}|\p{Currency_Symbol})+)(\d+(?:\.\d+)?)/u;

function separatePrefixUnit(input: string): { unit: string; number: string } | null {
  const [, unit, number] = input.match(PREFIX_UNIT_WITH_NUMBER_REGEX) ?? [];

  if (unit && number) {
    return {
      unit,
      number,
    };
  }

  return null;
}

function filterUnitSuggestions(
  input: string,
  suggestions: SingleSuggestion[],
  doc: DocumentModel,
): SingleSuggestion[] {
  const { index, node } = doc.cursor;

  return suggestions.filter((option) => {
    if (option.filterRules) {
      return checkFilterRules(option.filterRules, {
        index,
        node,
        selectedNodes: doc.selection.selectedNodes,
        selectedRequired: input.length === 0,
        text: doc.input.value,
      });
    }

    return false;
  });
}

function getUnitNode(
  node: NodeModel | undefined,
  placement: UnitNodePlacement,
): NumberNodeModel | VariableNodeModel | ConstantNodeModel | null {
  const passed = checkNodeCondition(
    {
      hasType: ["number", "variable", "constant"],
      hasUnit: [`not:${placement}`],
    },
    node,
  );

  if (passed) {
    return node as NumberNodeModel | VariableNodeModel | ConstantNodeModel;
  }

  return null;
}

function getPrefixUnitSuggestion(
  str: string,
  doc: DocumentModel,
  lang?: KeywordLang,
): SingleSuggestion[] {
  const input = str.toLowerCase();
  const { nodeAfterCursor } = doc.cursor;
  const unitNode = getUnitNode(nodeAfterCursor, "prefix");
  const prefixUnitWithNumber = separatePrefixUnit(input);

  const symbol = prefixUnitWithNumber ? prefixUnitWithNumber.number : unitNode?.value;

  const unitInput = prefixUnitWithNumber ? prefixUnitWithNumber.unit : input;

  if (!symbol) {
    return [];
  }

  const suggestionsData = filterSuggestions(
    unitInput,
    unitsWithType.map((unit) =>
      mapValueToUnitSuggestion(symbol, unit, "nodeAfter", false, unitNode?.type),
    ),
    { lang },
  );

  if (prefixUnitWithNumber) {
    if (suggestionsData.length > 0) {
      const suggestions = suggestionsData.map((suggestion) => ({
        ...suggestion,
        item: {
          ...suggestion.item,
          setChildren: suggestion.substitution?.setChildren,
        },
        substitution: undefined,
      }));

      return suggestions;
    }

    if (PREFIX_UNIT_REGEX.test(unitInput)) {
      return [createCustomUnitSuggestion(symbol, unitInput, "prefix", true)];
    }

    return [];
  }

  const filteredSuggestions = filterUnitSuggestions(unitInput, suggestionsData, doc);

  if (filteredSuggestions.length > 0) {
    return filteredSuggestions;
  }

  if (PREFIX_UNIT_REGEX.test(unitInput)) {
    return [createCustomUnitSuggestion(symbol, unitInput, "prefix")];
  }

  return [];
}

function getPostfixUnitSuggestion(
  str: string,
  doc: DocumentModel,
  lang?: KeywordLang,
): SingleSuggestion[] {
  const input = str.toLowerCase();
  const { nodeBeforeCursor } = doc.cursor;
  const unitNode = getUnitNode(nodeBeforeCursor, "postfix");
  const symbol = unitNode?.value;

  if (!symbol) {
    return [];
  }

  const suggestionsData = filterSuggestions(
    input,
    unitsWithType.map((unit) =>
      mapValueToUnitSuggestion(symbol, unit, "nodeBefore", false, unitNode?.type),
    ),
    { lang },
  );
  const filteredSuggestions = filterUnitSuggestions(input, suggestionsData, doc);

  if (filteredSuggestions.length > 0) {
    return filteredSuggestions;
  }

  if (POSTFIX_UNIT_REGEX.test(input) && unitNode.type === "number") {
    return [createCustomUnitSuggestion(symbol, str, "postfix")];
  }

  return [];
}

function getUnitDisplayText(unit: UnitWithType): string {
  const first = unit.children[0];
  if (first && "props" in first && first.props && "value" in first.props) {
    return first.props.value ?? "";
  }
  return "";
}

function mapInsideUnitSuggestion(unit: UnitWithType): SingleSuggestion {
  const displayText = getUnitDisplayText(unit);
  return {
    type: "single",
    item: {
      type: "plainText",
      intlName: "plainText",
      displayName: displayText,
      symbol: displayText,
      category: unit.name ?? "Unit",
      keywords: normalizeSuggestionKeywords({ keywords: unit.keywords }),
    },
    priority: 1.5,
    popupGroup: "units",
    canBeAutocompleted: { text: { regex: "\\P{Letter}$" }, math: { regex: "\\P{Letter}$" } },
  };
}

function getInsideUnitSuggestions(str: string, lang?: KeywordLang): SingleSuggestion[] {
  return filterSuggestions(str, unitsWithType.map(mapInsideUnitSuggestion), { lang });
}

export const unitSuggestionsProvider: SuggestionProvider = {
  type: "unit",
  search: (str, doc, lang) => {
    if (!str) {
      return [];
    }

    if (isUnitChild(doc.cursor.node)) {
      return [getInsideUnitSuggestions(str, lang)];
    }

    if (doc.input.editingNode) {
      return [];
    }

    const suggestions: SingleSuggestion[] = [
      ...getPrefixUnitSuggestion(str, doc, lang),
      ...getPostfixUnitSuggestion(str, doc, lang),
    ].map((s) => {
      const priority = prioritizeSuggestionRules(s, doc.cursor);
      return { ...s, priority };
    });

    return [suggestions];
  },
};
